import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cabeMaisUmDesafio, erroNasDatas, quantosAtivos } from "@nhomi/core";
import { nomeDaSessao, useSession } from "@/lib/auth";
import { useMe, usePlano } from "@/lib/plano";
import { watchMyChallenges, type Challenge } from "@/data/challenges";
import { dayKey } from "@/data/plates";
import { SemVaga } from "@/ui/SemVaga";
import { createChallenge } from "@/data/challenges";
import { ErrorNote, TopBar } from "@/ui/primitives";

/** "2026-08-06" no fuso de quem está olhando, sem passar por UTC. */
function dayKeyLocal(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function NewChallenge() {
  const { user } = useSession();
  const { config, plano, assinante } = usePlano();
  const me = useMe();
  const [meus, setMeus] = useState<Challenge[] | null>(null);

  /* A porta confere de novo, e não confia na Home ter escondido o botão:
     esta rota abre por link direto — e no caso do convite, é assim que
     ela SEMPRE abre. */
  useEffect(() => {
    if (!user) return;
    return watchMyChallenges(user.uid, setMeus);
  }, [user]);
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [startDay, setStartDay] = useState(dayKeyLocal());
  /* Vazio por padrão: a maioria dos grupos combina "vamos postar todo
     dia" e não tem data pra acabar. Quem quer uma disputa fechada, com
     vencedor e recap, preenche. */
  const [endDay, setEndDay] = useState("");
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  async function criar() {
    if (!user) return;
    const nome = name.trim();
    if (!nome) return setErro("Dá um nome pro desafio.");
    const erroDeData = erroNasDatas({ startDay, endDay }, dayKeyLocal());
    if (erroDeData) return setErro(erroDeData);

    setErro("");
    setSalvando(true);
    try {
      const cid = await createChallenge(
        {
          uid: user.uid,
          name: me?.name ?? nomeDaSessao(user),
          photo: me?.photo ?? "",
          email: user.email ?? "",
        },
        { name: nome, description: description.trim(), startDay, endDay },
      );
      navigate(`/c/${cid}/convite`, { replace: true });
    } catch (err) {
      setSalvando(false);
      setErro(err instanceof Error ? err.message : "Não deu pra criar o desafio.");
    }
  }

  /* Espera a lista chegar antes de decidir: mostrar "sem vaga" com a
     lista ainda em branco recusaria quem tem vaga de sobra. */
  const hoje = dayKey();
  const ativos = quantosAtivos(meus ?? [], hoje);
  if (meus && !assinante && !cabeMaisUmDesafio(config, plano, meus, hoje)) {
    return <SemVaga ativos={ativos} limite={config.desafiosGratis} />;
  }

  return (
    <main className="screen">
      <TopBar title="Novo desafio" back="/" />

      <div className="pad">
        {erro ? <ErrorNote>{erro}</ErrorNote> : null}

        <div className="card">
          <label className="field">
            <span className="field-label">Nome do desafio</span>
            <input
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Janta de agosto" maxLength={60} autoComplete="off"
            />
          </label>
          <label className="field">
            <span className="field-label">Descrição (opcional)</span>
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="As regras da brincadeira" maxLength={400}
            />
          </label>
        </div>

        <div className="row-2">
          <label className="card field">
            <span className="field-label">Começa</span>
            <input type="date" value={startDay} min={dayKeyLocal()} onChange={(e) => setStartDay(e.target.value)} />
          </label>
          <label className="card field">
            <span className="field-label">Termina <i className="opcional">opcional</i></span>
            <input type="date" value={endDay} min={startDay > dayKeyLocal() ? startDay : dayKeyLocal()} onChange={(e) => setEndDay(e.target.value)} />
          </label>
        </div>

        <p className="note">
          Sem data pra terminar, o desafio corre sem fim — é o normal pra
          um grupo que combina de postar todo dia. Com data, ele fecha e
          rende um recap com o vencedor do período.
        </p>

        <p className="note">
          Depois de criar você recebe um código de convite pra mandar no grupo.
          Quanto vale cada refeição, os selos e os eventos você ajusta depois.
        </p>

        <button className="btn btn-primary" onClick={criar} disabled={salvando}>
          {salvando ? "Criando…" : "Criar desafio"}
        </button>
      </div>
    </main>
  );
}
